import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface Site {
  siteUrl: string;
  permissionLevel: string;
}

interface Step2SelectSiteProps {
  sites: Site[];
  onNext: (data: { siteUrl: string; brandName?: string }) => void;
  onChange?: (data: { siteUrl: string; brandName?: string }) => void;
  isLoading: boolean;
  selectedSite?: string;
  method: "search_console" | "manual";
  domainInputRef?: React.RefObject<HTMLInputElement>;
  onBack?: () => void;
  initialDomain?: string;
  initialBrandName?: string;
  authError?: string | null;
}

import axios from "axios";

import { API_BASE_URL } from "@/config";

// ... imports

export function SiteSelection({ sites, onNext, onChange, isLoading, selectedSite: externalSelectedSite, method, domainInputRef, onBack, initialDomain = "", initialBrandName = "", authError }: Step2SelectSiteProps) {
  const [internalSelectedSite, setInternalSelectedSite] = useState("");
  const selectedSite = externalSelectedSite ?? internalSelectedSite;
  const [domain, setDomain] = useState(initialDomain);
  const [brandName, setBrandName] = useState(initialBrandName);
  const [search, setSearch] = useState("");
  const [isFetchingMetadata, setIsFetchingMetadata] = useState(false);
  const [error, setError] = useState("");
  const [isDomainBlocked, setIsDomainBlocked] = useState(false);
  const [manuallyAddedDomains, setManuallyAddedDomains] = useState<string[]>([]);

  // Fetch site metadata when site is selected (Search Console mode only)
  React.useEffect(() => {
    if (selectedSite && method === "search_console") {
      // Only fetch metadata for sites that are in Search Console (not manually added)
      const isSearchConsoleSite = sites.some(site => site.siteUrl === selectedSite);
      
      if (!isSearchConsoleSite) {
        // Skip fetching for manually added domains
        setIsFetchingMetadata(false);
        return;
      }
      
      setIsFetchingMetadata(true);
      
      // Debounce the fetch
      const timeoutId = setTimeout(async () => {
        try {
           // Always fetch and update when site changes
           const response = await axios.get(`${API_BASE_URL}/api/site-metadata?url=${encodeURIComponent(selectedSite)}`);
           const fetchedBrandName = response.data.site_name || "";
           setBrandName(fetchedBrandName);
           // Notify parent after metadata is fetched
           if (onChange) {
             onChange({ siteUrl: selectedSite, brandName: fetchedBrandName });
           }
        } catch (err: any) {
          console.error("Failed to fetch site metadata", err);
          if (err.response?.status === 403) {
            setIsDomainBlocked(true);
            setError("This domain has been blocked.");
          }
          setBrandName("");
          if (onChange) {
            onChange({ siteUrl: selectedSite, brandName: "" });
          }
        } finally {
          setIsFetchingMetadata(false);
        }
      }, 500); // 500ms debounce

      return () => clearTimeout(timeoutId);
    }
  }, [selectedSite, sites, method, onChange]);

  const cleanUrl = (url: string) => {
    let cleaned = url.replace(/^https?:\/\//, "");
    cleaned = cleaned.replace(/^www\./, "");
    return cleaned.replace(/\/$/, "");
  };

  const deriveBrandName = (url: string) => {
    const cleaned = cleanUrl(url);
    const parts = cleaned.split('.');
    if (parts.length > 0 && parts[0]) {
      return parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
    }
    return "";
  };

  // Simple domain validation
  const isValidDomain = (domain: string) => {
    // Basic validation: at least one dot, no spaces, at least 1 char before dot and 2 after
    // We clean the domain first before validating
    const cleaned = cleanUrl(domain);
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?\.[a-zA-Z]{2,}$/;
    return domainRegex.test(cleaned);
  };

  const processedSites = sites
    .map((site) => ({
      ...site,
      displayUrl: cleanUrl(site.siteUrl),
    }))
    .sort((a, b) => a.displayUrl.localeCompare(b.displayUrl));

  // Add manually added domains to the list
  const manualDomainSites = manuallyAddedDomains.map(domain => ({
    siteUrl: domain,
    displayUrl: cleanUrl(domain),
    permissionLevel: 'manual'
  }));

  const allSites = [...processedSites, ...manualDomainSites];

  const filteredSites = allSites.filter((site) =>
    site.displayUrl.toLowerCase().includes(search.toLowerCase())
  );

  // Auto-select if only one site
  React.useEffect(() => {
    if (filteredSites.length === 1 && !selectedSite) {
      setInternalSelectedSite(filteredSites[0].siteUrl);
    }
  }, [filteredSites, selectedSite]);

  const handleSiteChange = (siteUrl: string) => {
    setInternalSelectedSite(siteUrl);
    setError(""); // Clear error when user selects a site
    setIsDomainBlocked(false); // Reset blocked state
    // Also notify parent immediately so the preview updates
    if (onChange) {
      onChange({ siteUrl, brandName });
    }
  };

  const handleAddManualDomain = () => {
    // Set the domain from search input as the selected site
    const manualDomain = search;
    console.log("Adding manual domain:", manualDomain);
    setInternalSelectedSite(manualDomain);
    setDomain(manualDomain);
    setSearch(""); // Clear search to show the domain in the list
    setError("");
    // Reset brand name so metadata fetch can populate it fresh
    setBrandName("");
    
    // Add to manually added domains list if not already there
    if (!manuallyAddedDomains.includes(manualDomain)) {
      setManuallyAddedDomains(prev => [...prev, manualDomain]);
    }
    
    // Fetch metadata immediately for manually added domain
    setIsFetchingMetadata(true);
    // Use raw domain for API call
    axios.get(`${API_BASE_URL}/api/site-metadata?url=${encodeURIComponent(manualDomain)}`)
      .then(response => {
        const fetchedBrandName = response.data.site_name || "";
        console.log("Fetched brand name for manual domain:", fetchedBrandName);
        setBrandName(fetchedBrandName);
        if (onChange) {
          // Use raw domain for parent update
          onChange({ siteUrl: manualDomain, brandName: fetchedBrandName });
        }
      })
      .catch((err: any) => {
        console.error("Failed to fetch site metadata for manual domain:", err);
        if (err.response?.status === 403) {
          setIsDomainBlocked(true);
          setError("This domain has been blocked.");
          return;
        }
        const fallbackBrand = deriveBrandName(manualDomain);
        setBrandName(fallbackBrand);
        if (onChange) {
          onChange({ siteUrl: manualDomain, brandName: fallbackBrand });
        }
      })
      .finally(() => {
        setIsFetchingMetadata(false);
      });
  };

  // Fetch metadata for manual domain entry and notify parent after
  React.useEffect(() => {
    // Clean domain before checking validity
    
    if (method === "manual" && domain && isValidDomain(domain)) {
      const fetchMetadata = async () => {
        setIsFetchingMetadata(true);
        try {
          // Use raw domain for API call
          console.log("Fetching metadata for:", domain);
          const response = await axios.get(`${API_BASE_URL}/api/site-metadata?url=${encodeURIComponent(domain)}`);
          console.log("Metadata response:", response.data);
          const fetchedBrandName = response.data.site_name || deriveBrandName(domain);
          console.log("Final brand name:", fetchedBrandName);
          setBrandName(fetchedBrandName);
          // Notify parent after metadata is fetched
          if (onChange) {
            // Pass raw URL to parent
            onChange({ siteUrl: domain, brandName: fetchedBrandName });
          }
        } catch (err: any) {
          console.error("Failed to fetch site metadata", err);
          if (err.response?.status === 403) {
            setIsDomainBlocked(true);
            setError("This domain has been blocked.");
            return;
          }
          const fallbackBrand = deriveBrandName(domain);
          setBrandName(fallbackBrand);
          // Notify parent even if fetch fails
          if (onChange) {
            // Pass raw URL to parent even on error
            onChange({ siteUrl: domain, brandName: fallbackBrand });
          }
        } finally {
          setIsFetchingMetadata(false);
        }
      };
      
      // Debounce the fetch to avoid too many requests while typing
      const timeoutId = setTimeout(fetchMetadata, 500);
      return () => {
        clearTimeout(timeoutId);
        setIsFetchingMetadata(false);
      };
    } else if (method === "manual") {
      // Reset loading state if domain is invalid or empty
      setIsFetchingMetadata(false);
    }
  }, [domain, method, onChange]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (isDomainBlocked) {
      setError("This domain has been blocked.");
      return;
    }

    // Validate based on method
    if (method === "manual") {
      if (!domain) {
        setError("Please enter a domain");
        return;
      }
      // Check validity of cleaned domain
      if (!isValidDomain(domain)) {
         setError("Please enter a valid domain (e.g. example.com)");
         return;
      }
    } else {
      if (!selectedSite) {
        setError("Please select a domain from the list");
        return;
      }
    }
    
    // Clear error and proceed
    setError("");
    const siteUrl = method === "manual" ? domain : selectedSite;
    
    // Pass raw URL (backend handles parsing if needed)
    onNext({ siteUrl, brandName: brandName || "" });
  };

  return (
    <div className="w-full max-w-md mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* Back button for manual mode */}
      {method === "manual" && onBack && (
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-950 transition-colors"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
      )}
      
      <div className="space-y-2">
        <h1 className="text-xl font-semibold tracking-tight text-slate-950">
          {method === "manual" ? "Enter Your Domain" : "Select Your Domain"}
        </h1>
        <p className="text-gray-500 text-sm">
          {method === "manual" 
            ? "Add your website to generate LLM ready prompts." 
            : "Select your website to generate LLM ready prompts."}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {method === "manual" ? (
          /* Manual Domain Entry */
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="domain">Domain</Label>
              {error && method === "manual" && (
                <p className="text-sm font-medium text-red-600 leading-none">{error}</p>
              )}
            </div>
            <Input
              ref={domainInputRef}
              id="domain"
              placeholder="example.com"
              value={domain}
              onChange={(e) => {
                const val = e.target.value;
                setDomain(val);
                setError(""); // Clear error when user starts typing
                setIsDomainBlocked(false); // Reset blocked state
                
                // Update parent immediately with raw domain so preview updates
                if (onChange) {
                   // Only update if it looks somewhat like a domain or is empty
                   onChange({ siteUrl: val, brandName: brandName });
                }
              }}
              className="bg-white"
            />
          </div>
        ) : (
          /* Search Console Property List */
          <>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm">Domain</Label>
                {search && filteredSites.length === 0 && (
                  <p className="text-sm font-medium text-red-600 leading-none">No domain found</p>
                )}
                {error && method === "search_console" && !search && (
                  <p className="text-sm font-medium text-red-600 leading-none">{error}</p>
                )}
              </div>
              <Input
                placeholder="example.com"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="bg-white"
              />
            </div>

            <div className="max-h-64 overflow-y-auto rounded-md border border-slate-200 bg-white p-2 shadow-sm">
              {isLoading ? (
                <div className="flex h-full items-center justify-center text-slate-400">
                  Loading sites...
                </div>
              ) : authError ? (
                <div className="flex flex-col h-full items-center justify-center gap-3 p-4 text-center">
                  <div className="rounded-full bg-red-100 p-2">
                    <svg className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-slate-900">Connection Failed</p>
                    <p className="text-xs text-slate-500">
                      {authError === "missing_scope" 
                        ? "You must allow access to Search Console data to continue." 
                        : "Failed to load your sites from Google."}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={onBack}
                    className="text-xs font-medium text-blue-600 hover:text-blue-700 underline"
                  >
                    Try Again
                  </button>
                </div>
              ) : filteredSites.length === 0 ? (
                <div className="flex flex-col h-full items-center justify-center gap-2">
                  {search && isValidDomain(search) ? (
                    <button
                      type="button"
                      onClick={handleAddManualDomain}
                      className="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
                    >
                      + Add "{search}"
                    </button>
                  ) : search ? (
                    <p className="text-xs text-slate-400">Enter a valid domain to add it</p>
                  ) : (
                    <p className="text-sm text-slate-400">No sites found</p>
                  )}
                </div>
              ) : (
                <div className="space-y-1">
                  {filteredSites.map((site) => {
                    return (
                      <label
                        key={site.siteUrl}
                        className={`flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 transition-colors ${
                          selectedSite === site.siteUrl
                            ? "bg-slate-100 text-slate-950"
                            : "hover:bg-slate-50 text-slate-950"
                        }`}
                      >
                        <input
                          type="radio"
                          name="site"
                          value={site.siteUrl}
                          checked={selectedSite === site.siteUrl}
                          onChange={(e) => handleSiteChange(e.target.value)}
                          className="h-4 w-4 border-slate-300 text-slate-950 focus:ring-slate-950"
                        />
                        <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded border bg-white">
                          <img
                            src={`https://www.google.com/s2/favicons?domain=${site.siteUrl}&sz=64`}
                            alt=""
                            className="h-4 w-4 object-contain"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                            }}
                          />
                        </div>
                        <span className="truncate text-sm font-medium flex-1">
                          {site.displayUrl}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}

        <div className="space-y-2">
          <Label htmlFor="brandName">Brand Name (Optional)</Label>
          <div className="relative">
            <Input
              id="brandName"
              placeholder="e.g. Acme"
              value={brandName}
              onChange={(e) => {
                const newBrandName = e.target.value;
                setBrandName(newBrandName);
                // Notify parent immediately when user types
                if (onChange) {
                  const currentSiteUrl = method === "manual" ? domain : selectedSite;
                  if (currentSiteUrl) {
                    // Pass raw URL to parent for preview updates
                    onChange({ siteUrl: currentSiteUrl, brandName: newBrandName });
                  }
                }
              }}
              className={isFetchingMetadata ? "pr-8 bg-white" : "bg-white"}
              disabled={false}
            />
            {isFetchingMetadata && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600" />
              </div>
            )}
          </div>
        </div>

        <button
          type="submit"
          className="w-full rounded-md inline-flex items-center justify-center whitespace-nowrap font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 bg-blue-500 text-primary-foreground hover:bg-blue-500/90 h-9 px-3 text-sm"
        >
          Generate Prompts
        </button>
      </form>
    </div>
  );
}
